import { LTCaseUserOption } from "./enum/LTCaseUserOption";

/**
 * Defines which mesh keys are available for each option type.
 * - Options not listed have no mesh support
 * - "default" is the standard single-mesh key
 * - Named keys like "top"/"bottom" indicate multiple mesh slots
 */
export type MeshKeyDefinition = {
  key: string;
  label: string;
  required?: boolean;
};

export const OptionMeshKeyDefinitions: Partial<
  Record<LTCaseUserOption, MeshKeyDefinition[]>
> = {
  // Hardware with top/bottom meshes
  [LTCaseUserOption.LockType]: [
    { key: "top", label: "Oberteil", required: true },
    { key: "bottom", label: "Unterteil" },
  ],
  [LTCaseUserOption.HingeType]: [
    { key: "top", label: "Oberteil", required: true },
    { key: "bottom", label: "Unterteil" },
  ],
  [LTCaseUserOption.PianoHingeType]: [
    { key: "top", label: "Oberteil", required: true },
    { key: "bottom", label: "Unterteil" },
  ],

  // Corners with default and angle variant
  [LTCaseUserOption.CornerType]: [
    { key: "default", label: "Standard", required: true },
    { key: "angleVariant", label: "L-Variante" },
  ],

  // Parts with single default mesh
  [LTCaseUserOption.FootType]: [
    { key: "default", label: "Mesh", required: true },
  ],
  [LTCaseUserOption.HandleType]: [
    { key: "default", label: "Mesh", required: true },
  ],
  [LTCaseUserOption.DishType]: [
    { key: "default", label: "Mesh", required: true },
  ],
  [LTCaseUserOption.TrolleyHandleType]: [
    { key: "default", label: "Mesh", required: true },
  ],
  [LTCaseUserOption.CornerBraceType]: [
    { key: "default", label: "Standard (lange Kante)", required: true },
    { key: "angleVariant", label: "Variante (kurze Kante)" },
  ],
  [LTCaseUserOption.ProfileType]: [{ key: "default", label: "Mesh" }],

  // Rollers - all types use only default mesh
  [LTCaseUserOption.RollerType]: [
    { key: "default", label: "Mesh", required: true },
  ],
};

/**
 * Get the mesh key definitions for an option type.
 * Returns undefined for options that don't support meshes.
 */
export function getMeshKeysForOptionType(
  kind: LTCaseUserOption | string | undefined,
): MeshKeyDefinition[] | undefined {
  if (!kind) return undefined;
  return OptionMeshKeyDefinitions[kind as LTCaseUserOption];
}

/**
 * Options that have no mesh/cutout support at all.
 */
export const OptionsWithoutMeshSupport: ReadonlySet<LTCaseUserOption> = new Set(
  [
    LTCaseUserOption.AngleSectionType,
    LTCaseUserOption.PanelColor,
    LTCaseUserOption.PanelStrength,
    LTCaseUserOption.PaddingType,
    LTCaseUserOption.RackRailType,
    LTCaseUserOption.RunnerType,
    LTCaseUserOption.TrayMaterialType,
  ],
);
